import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { motion } from "framer-motion";
import {
  FileText,
  Download,
  Ban,
  Link,
  Upload,
  Clock,
  CheckCheck,
  CloudUpload,
  X,
  File,
  FileQuestion
} from "lucide-react";

import styles from "./SubProjectInformationV2.module.scss";
import { sendBudget, getSubProjectBudgets } from "../../../../store/actions";
import RegisteredUserProfileLoader from "../../../RegisteredUsers/RegisteredUsersSkeleton/RegisteredUserProfileLoader";

const variants = {
  hidden: { opacity: 0, scale: 0.5 },
  visible: { opacity: 1, scale: 1 },
};

interface SubProjectInformationV2Props {
  subproyecto: any; 
  postulationStatus: string | null; 
  sendBudget: (archivo: File, subProjectId: string, mainProjectId: string) => void; 
  userData: any; 
  type?: string;
  getSubProjectBudgets: (subProjectId: string) => void; 
  mainProjectId: string;
  subProjectBudgets: any; 
  handlePostulationButton: () => void;
  isMyProject?: boolean; 
}

const SubProjectInformationV2: React.FC<SubProjectInformationV2Props> = ({
  subproyecto,
  postulationStatus,
  sendBudget,
  userData,
  type,
  getSubProjectBudgets,
  mainProjectId,
  subProjectBudgets,
  handlePostulationButton,
  isMyProject = false,
}) => {
  const [fileName, setFileName] = useState<string>(
    (subproyecto && subproyecto.fileName) || ""
  );
  const [budgetToSend, setBudgetToSend] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  const projectIdWithSubprojectName = mainProjectId + '_' + subproyecto.name;


  useEffect(() => {
    if (subproyecto?.id) {
      getSubProjectBudgets(subproyecto.id);
    }
  }, [getSubProjectBudgets, subproyecto?.id]);

  const uploadFile = (file: File) => {
    if (!file) {
      return;
    }
    setBudgetToSend(file);
    setFileName(file.name);
  };

  const handleSendBudget = () => {
    if (!budgetToSend) {
      console.warn('No hay archivo seleccionado para enviar');
      return;
    }
    
    sendBudget(budgetToSend, subproyecto.id, projectIdWithSubprojectName);
  };

  const hasSentBudget = useMemo(() => {
    if (!subProjectBudgets || !subproyecto?.id || !userData?.id) {
      return false;
    }
    
    return subProjectBudgets[subproyecto.id]?.some(
      (budget: any) => budget.user.id === userData.id
    ) || false;
  }, [subProjectBudgets, subproyecto?.id, userData?.id]);

  useEffect(() => {
    if ((subProjectBudgets && subproyecto?.id) || hasSentBudget) {
      setIsLoading(false);
    }
  }, [subProjectBudgets, subproyecto?.id, hasSentBudget]);

  const hasBudgetPayroll = subproyecto?.budget_payroll !== null && subproyecto?.budget_payroll !== undefined;
  const hasPlanos = subproyecto?.planos && subproyecto?.planos !== "null" && subproyecto?.planos !== "";
  const hasMetricComputation = subproyecto?.metric_computation !== null && subproyecto?.metric_computation !== undefined;
  const hasTechnicalSpecifications = subproyecto?.technical_specifications !== null && subproyecto?.technical_specifications !== undefined;
  const hasLink = subproyecto?.link && subproyecto?.link !== "";

  const noDocus = !hasBudgetPayroll && !hasPlanos && !hasMetricComputation && !hasTechnicalSpecifications && !hasLink;

  return (
    <div className={styles.container}>
      {noDocus ? (
        <div className={styles.container_noDocus}>
          <Ban size={20} className={styles.container_noDocus_icon} />
          <p>
            {isMyProject 
              ? "No has adjuntado documentación específica para este subproyecto" 
              : "Lamentablemente el usuario no adjuntó documentación específica"
            }
          </p>
        </div>
      ) : (
        <div className={styles.container_body}>
          {hasLink && (
            <div className={styles.container_body_item}>
              <div className={styles.container_body_item_label}>
                <FileText size={20} />
                Link a los planos
              </div>
              <a href={subproyecto?.link} target="_blank" rel="noreferrer" className={`${styles.container_body_item_icon} ${postulationStatus !== "ALLOWED" && styles.forbidden}`}>
                <Link size={16} />
                Ir al link
              </a>
            </div>
          )}
          {hasPlanos && (
            <div className={styles.container_body_item}>
              <div className={styles.container_body_item_label}>
                <FileText size={20} />
                Planos
              </div>
              <a href={subproyecto?.planos} target="_blank" rel="noreferrer" className={`${styles.container_body_item_icon} ${postulationStatus !== "ALLOWED" && styles.forbidden}`}>
                <Download size={16} />
                Descargar
              </a>
            </div>
          )}
          {hasBudgetPayroll && (
            <div className={styles.container_body_item}>
              <div className={styles.container_body_item_label}>
                <FileText size={20} />
                Planilla de cotización
              </div>
              <a href={subproyecto?.budget_payroll} target="_blank" rel="noreferrer" className={`${styles.container_body_item_icon} ${postulationStatus !== "ALLOWED" && styles.forbidden}`}>
                <Download size={16} />
                Descargar
              </a>
            </div>
          )}
          {hasMetricComputation && (
            <div className={styles.container_body_item}>
              <div className={styles.container_body_item_label}>
                <FileText size={20} />
                Cómputo métrico
              </div>
              <a href={subproyecto?.metric_computation} target="_blank" rel="noreferrer" className={`${styles.container_body_item_icon} ${postulationStatus !== "ALLOWED" && styles.forbidden}`}>
                <Download size={16} />
                Descargar
              </a>
            </div>
          )}
          {hasTechnicalSpecifications && (
            <div className={styles.container_body_item}>
              <div className={styles.container_body_item_label}>
                <FileText size={20} />
                Especificaciones técnicas
              </div>
              <a href={subproyecto?.technical_specifications} target="_blank" rel="noreferrer" className={`${styles.container_body_item_icon} ${postulationStatus !== "ALLOWED" && styles.forbidden}`}>
                <Download size={16} />
                Descargar
              </a>
            </div>
          )}
        </div>
      )}
            
      {!isMyProject && (
        <>
          {isLoading ? (
            <div className={styles.container_budget}>
              <RegisteredUserProfileLoader smallVersion={true} noBackground={true} />
            </div>
          ) : (
               !postulationStatus ? (
                <div className={styles.container_budget}>
                  <button className={styles.container_budget_accessRequestBtn} onClick={handlePostulationButton}>
                    <FileQuestion size={20} />
                    <p>Solicitar acceso a la documentación</p>
                  </button>
                </div>
              ) : (
                <div className={styles.container_budget}>
                  {postulationStatus === "NEGATED" && (
                    <button className={styles.container_budget_deniedBtn}>
                      <X size={20} />
                      <p>Postulación negada</p>
                    </button>
                  )}
                  {postulationStatus === "SEND" && (
                    <button className={styles.container_budget_sendBtn}>
                      <Clock size={20} />
                      <p>Esperando respuesta</p>
                    </button>
                  )}
                  {postulationStatus === "ALLOWED" && !hasSentBudget && (
                    <div className={styles.container_budget_sendBudget}>
                      <div className={styles.container_budget_sendBudget_inputContainer}>
                        <div className={styles.container_budget_sendBudget_inputContainer_header}>
                          <h4 className={styles.container_budget_sendBudget_inputContainer_header_title}>Subir presupuesto</h4>
                          <p className={styles.container_budget_sendBudget_inputContainer_header_sub} >Sube tu archivo de presupuesto en formato Excel (.xlsx) o PDF (.pdf)</p>
                        </div>
                        {!fileName ? (
                          <motion.div
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            variants={variants}
                            transition={{ duration: 0.5 }}
                          >
                            <label
                              htmlFor="file"
                              className={styles.container_budget_sendBudget_label}>
                              <CloudUpload size={40} style={{ color: "rgb(156 163 175)" }} />
                              <span style={{ color: "rgb(75 85 99)", fontSize: "1.6rem" }}>Arrastra y suelta tu archivo aquí o</span>
                              <span className={styles.container_budget_sendBudget_label_sub}>selecciona un archivo</span>
                              <span>Archivos permitidos: XLSX, PDF, XLS</span>
                              <input
                                type="file"
                                onChange={(e) => uploadFile(e.target.files![0])}
                                className={styles.container_budget_inputFile}
                                accept=".xlsx, .pdf, .xls"
                                id="file"
                                style={{ display: "none" }}
                              />
                            </label>
                          </motion.div>
                        ) : (
                          <div className={styles.container_budget_sendBudget_file}>
                            <div className={styles.container_budget_sendBudget_file_itemContainer}>
                              <div className={styles.container_budget_sendBudget_file_icon}>
                                <File size={20} />
                              </div>
                              <p className={styles.container_budget_sendBudget_file_name}>{fileName}</p>
                            </div>
                            <button
                              className={styles.container_budget_sendBudget_file_delete}
                              onClick={() => {
                                setFileName("");
                                setBudgetToSend(null);
                              }}
                            >
                              <X size={20} />
                            </button>
                          </div>
                        )}
                      </div>

                      <button 
                        className={styles.container_budget_acceptedBtn} 
                        onClick={handleSendBudget}
                        disabled={!budgetToSend}
                      >
                        <Upload size={20} />
                        <p>Enviar presupuesto</p>
                      </button>
                    </div>
                  )}
                  {postulationStatus === "ALLOWED" && hasSentBudget && (
                    <button className={styles.container_budget_sentBtn}>
                      <CheckCheck size={20} />
                      Ya enviaste tu presupuesto
                    </button>
                  )}
                </div>
              )
              )}
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    userData: state.general.userData,
    subProjectBudgets: state.profile.subProjectBudgets,
  };
};

export default connect(mapStateToProps, { sendBudget, getSubProjectBudgets })(SubProjectInformationV2);
